<script setup lang="ts">
import {
  IconArrowCircleDownFill,
  IconArrowCircleUpFill,
  IconHomeFill,
  IconQrcodeRegular,
  IconUserFill,
} from "@packages/icon-library";
import { IUser, useUserStore } from "@/modules/user-module";
import { computed, ComputedRef, ref } from "vue";
import { MobileEarnDeductModal } from "@/modules/app-mobile-nav";
import { getUserAvatar } from "@/package/global-helpers/user-helpers";
import { useI18n } from "vue-i18n";

const { t } = useI18n();
const userStore = useUserStore();
const user: ComputedRef<null | IUser> = computed(() => userStore.user);
const showEarnDeductModal = ref(false);
</script>

<template>
  <MobileEarnDeductModal
    v-if="showEarnDeductModal"
    @close="showEarnDeductModal = false"
  />

  <nav class="app-mobile-nav" :class="{ 'app-mobile-nav_auth': user }">
    <RouterLink :to="{ name: 'HomeView' }">
      <IconHomeFill />
      {{ t("nav.home") }}
    </RouterLink>
    <RouterLink :to="{ name: 'ReceiveView' }">
      <IconArrowCircleDownFill />
      {{ t("nav.receive") }}
    </RouterLink>
    <button v-if="user" type="button" @click="showEarnDeductModal = true">
      <IconQrcodeRegular />
    </button>
    <RouterLink :to="{ name: 'SpendView' }">
      <IconArrowCircleUpFill />
      {{ t("nav.spend") }}
    </RouterLink>
    <RouterLink :to="{ name: 'ProfileView' }">
      <template v-if="!user">
        <IconUserFill />
      </template>

      <span
        v-else
        class="avatar"
        :style="{ backgroundImage: `url(${getUserAvatar(user)})` }"
      />

      <template v-if="user">{{ t("nav.profile") }}</template>

      <template v-else>{{ t("system.auth") }}</template>
    </RouterLink>
  </nav>
</template>

<style scoped lang="scss">
.app-mobile-nav {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: var(--color-semantic-background-normal-bg-primary);
  border-top: 1px solid var(--color-semantic-border-normal-soft);
  z-index: 100;
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 1fr;
  align-items: center;
  justify-content: center;
  height: 60px;

  &_auth {
    grid-template-columns: 1fr 1fr 60px 1fr 1fr;
  }

  > a {
    @include caption();
    color: var(--color-semantic-content-normal-tertiary);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-flow: column;
    gap: calc($base-space / 2);

    > .icon {
      width: 20px;
      height: 20px;
      background-color: var(--color-semantic-content-normal-tertiary);
    }

    > .avatar {
      width: 20px;
      height: 20px;
      border-radius: 50%;
      border: 0.5px solid var(--color-semantic-border-normal-soft);
      @include centerBackgroundImageCover();
    }

    &.router-link-active {
      color: var(--color-semantic-content-normal-primary);

      > .icon {
        background-color: var(--color-semantic-content-normal-primary);
      }
    }
  }

  > button {
    width: 44px;
    height: 44px;
    border-radius: 50%;
    background-color: var(--color-semantic-background-normal-contrast);
    margin: ($base-space * 2);
    display: flex;
    align-items: center;
    justify-content: center;

    > .icon {
      width: 20px;
      height: 20px;
      background-color: var(--color-semantic-background-normal-bg-primary);
    }
  }
}
</style>
